import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { getLocalDate, getToken, URL } from "../../utils";
import ChartFullBg from "../../components/ChartFullBg";
import ConditionalRender from "../../components/ConditionalRender";
import { useParams } from "react-router-dom";
import axios from "axios";
import Purchases from "../../components/Purchases";
import ClientDegustations from "../../components/ClientDegustations";

export default function Particulier() {
  const [currentClient, setCurrentClient] = useState({});
  const [loading, setLoading] = useState(true);
  const [statisticData, setStatisticData] = useState({
    week: [],
    month: [],
    year: [],
  });
  const [purchases, setPurchases] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("month");
  const token = getToken();
  const { id } = useParams();

  // Fonction pour formater les numéros de téléphone
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return "";
    // Format: XX XX XX XX XX
    return phoneNumber.replace(
      /(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/,
      "$1 $2 $3 $4 $5",
    );
  };

  useEffect(() => {
    const fetchClient = async () => {
      try {
        setLoading(true);
        const [
          response,
          weekDataResponse,
          monthDataResponse,
          yearDataResponse,
          purchaseData,
        ] = await Promise.all([
          axios.get(
            `${URL}Clients/detail-client-particulier/${id}`, // URL
            {
              headers: {
                "Content-Type": "application/json", // Format des données
                Authorization: `Bearer ${token}`, // Token
              },
            },
          ),
          axios.get(
            URL + "Declarations/ventes/graphe-achats-by-client-part_w/" + id,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            },
          ),
          axios.get(
            URL + "Declarations/ventes/graphe-achats-by-client-part_m/" + id,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            },
          ),
          // Essayons l'URL correcte pour les données annuelles
          axios.get(
            URL + "Declarations/ventes/graphe-achats-by-client-part_y/" + id,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            },
          ),
          axios.get(
            URL + "Declarations/ventes/listes-achats-by-client-part/" + id,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            },
          ),
        ]);

        setCurrentClient(response.data);

        // Utiliser les vraies données de l'API
        setPurchases(purchaseData.data || []);

        // Pour les données hebdomadaires, regroupons par jour avec format jour + mois
        const weekTemp = { data: [], labels: [] };
        const weekDataGrouped = {};

        weekDataResponse.data.ventes_par_periode.forEach((item) => {
          const date = new Date(item.date_sortie__date);
          const formattedDate = date.toLocaleDateString("fr-FR", {
            day: "numeric",
            month: "long",
          });

          if (!weekDataGrouped[formattedDate]) {
            weekDataGrouped[formattedDate] = 0;
          }
          weekDataGrouped[formattedDate] += item.total;
        });

        // Conversion du dictionnaire en tableaux ordonnés
        Object.keys(weekDataGrouped).forEach((date) => {
          weekTemp.labels.push(date);
          weekTemp.data.push(weekDataGrouped[date]);
        });

        // Pour les données mensuelles, regroupons par jour avec format jour + mois + année
        const monthTemp = { data: [], labels: [] };
        const monthDataGrouped = {};

        monthDataResponse.data.ventes_par_periode.forEach((item) => {
          const date = new Date(item.date_sortie__date);
          const formattedDate = date.toLocaleDateString("fr-FR", {
            day: "numeric",
            month: "long",
            year: "numeric",
          });

          if (!monthDataGrouped[formattedDate]) {
            monthDataGrouped[formattedDate] = 0;
          }
          monthDataGrouped[formattedDate] += item.total;
        });

        // Conversion du dictionnaire en tableaux ordonnés
        // Utilisons les objets Date pour trier correctement
        const dateKeys = Object.keys(monthDataGrouped);
        const sortedDates = dateKeys
          .map((dateStr) => {
            const parts = dateStr.split(" ");
            const day = parseInt(parts[0]);
            const month = [
              "janvier",
              "février",
              "mars",
              "avril",
              "mai",
              "juin",
              "juillet",
              "août",
              "septembre",
              "octobre",
              "novembre",
              "décembre",
            ].indexOf(parts[1]);
            const year = parseInt(parts[2]);
            return {
              dateStr,
              dateObj: new Date(year, month, day),
              value: monthDataGrouped[dateStr],
            };
          })
          .sort((a, b) => a.dateObj - b.dateObj);

        sortedDates.forEach((item) => {
          monthTemp.labels.push(item.dateStr);
          monthTemp.data.push(item.value);
        });

        // Pour les données annuelles, regroupons par mois
        const yearTemp = { data: [], labels: [] };

        // Vérifions si la structure attendue existe
        if (yearDataResponse.data.ventes_par_periode) {
          yearDataResponse.data.ventes_par_periode.forEach((item) => {
            const total = item.total || 0; // Assurer une valeur par défaut
            yearTemp.data.push(total);

            // Pour l'année, afficher uniquement le mois
            let dateToFormat = item.mois
              ? new Date(item.mois)
              : new Date(item.date_sortie__date || "");
            const monthName = dateToFormat.toLocaleString("fr-FR", {
              month: "long",
            });
            yearTemp.labels.push(monthName);
          });
        }

        setStatisticData({
          week: weekTemp,
          month: monthTemp,
          year: yearTemp,
        });

        // toast.success("Client récupéré avec succès");
      } catch (error) {
        toast.error("Erreur lors de la récupération du client");
        console.error("Erreur lors de la récupération des données :", error);
      }
      setLoading(false);
    };

    fetchClient();
  }, [id, token]); // Ajout de 'id' et 'token' comme dépendances

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-t-4 border-amber-500 rounded-full animate-spin my-4"></div>
          <p className="text-amber-600 font-medium">
            Chargement des données...
          </p>
        </div>
      </div>
    );
  }

  console.log("Données du client :", currentClient);

  return (
    currentClient && (
      <>
        <ToastContainer />
        {/* En-tête avec information principale et bouton d'action */}
        <div className="bg-white shadow-md rounded-md m-2 mb-4">
          <div className="p-4 flex flex-col md:flex-row justify-between items-start md:items-center">
            <div className="flex items-center gap-4 mb-4 md:mb-0">
              <div className="p-3 bg-amber-100 rounded-full text-amber-600 grid justify-center items-center">
                <svg
                  className="w-8 h-8"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0 0a9 9 0 0 0 5-1.5 4 4 0 0 0-4-3.5h-2a4 4 0 0 0-4 3.5 9 9 0 0 0 5 1.5Zm3-11a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                  />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">{`${
                  currentClient.nom
                } ${currentClient.prenom || ""}`}</h1>
                <div className="flex flex-wrap gap-2 mt-1">
                  {currentClient.profession && (
                    <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">
                      {currentClient.profession}
                    </span>
                  )}
                  <span className="bg-amber-100 text-amber-700 text-xs px-2 py-1 rounded-full">
                    {currentClient.sexe === "M" ? "Masculin" : "Féminin"}
                  </span>
                  {currentClient.is_father && (
                    <span className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full">
                      Père
                    </span>
                  )}
                  {currentClient.is_mother && (
                    <span className="bg-pink-100 text-pink-700 text-xs px-2 py-1 rounded-full">
                      Mère
                    </span>
                  )}
                  {currentClient.is_grand_father && (
                    <span className="bg-indigo-100 text-indigo-700 text-xs px-2 py-1 rounded-full">
                      Grand-père
                    </span>
                  )}
                  {currentClient.is_grand_mother && (
                    <span className="bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded-full">
                      Grand-mère
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <div className="bg-amber-50 rounded-md p-3 text-amber-800 text-center">
                <p className="text-xs uppercase font-semibold">
                  Chiffre d'affaire
                </p>
                <p className="text-lg font-bold">
                  {currentClient.chiffre_affaire || "0 FCFA"}
                </p>
              </div>
              <ConditionalRender requiredPermissions="canCreateDegustation">
                <button
                  onClick={() => {
                    // Scroll to degustations section and trigger modal
                    const degustationsSection = document.querySelector(
                      '[data-section="degustations"]',
                    );
                    if (degustationsSection) {
                      degustationsSection.scrollIntoView({
                        behavior: "smooth",
                      });
                      // Trigger the create degustation modal
                      const createButton = degustationsSection.querySelector(
                        'button[data-action="create"]',
                      );
                      if (createButton) {
                        setTimeout(() => createButton.click(), 500);
                      }
                    }
                  }}
                  className="px-4 py-2 rounded-md bg-green-500 text-white hover:bg-green-600 focus:ring-2 focus:ring-green-400 transition-colors flex items-center gap-2"
                  title="Nouvelle dégustation"
                >
                  + Dégustation
                </button>
              </ConditionalRender>
              <ConditionalRender requiredPermissions="canEditClient">
                <Link
                  to={["/clients/particuliers/edit/", id].join("")}
                  className="px-4 py-2 rounded-md bg-amber-100 text-amber-600 hover:bg-amber-600 hover:text-white transition-colors flex items-center gap-2"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                    />
                  </svg>
                  Modifier
                </Link>
              </ConditionalRender>
            </div>
          </div>
          <div className="border-t border-gray-100 px-4 py-3 flex flex-wrap gap-x-8 gap-y-2 text-sm">
            {currentClient.contact1 && (
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
                <span>{currentClient.contact1}</span>
              </div>
            )}
            {currentClient.email && (
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                <span>{currentClient.email}</span>
              </div>
            )}
            {(currentClient.country || currentClient.pays) && (
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <span>{currentClient.country || currentClient.pays}</span>
              </div>
            )}
            {currentClient.nationalite && (
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-gray-500"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 12c0 4.9706-4.0294 9-9 9m9-9c0-4.97056-4.0294-9-9-9m9 9h-5m-4 9c-4.97056 0-9-4.0294-9-9m9 9v-5m-9-4c0-4.97056 4.02944-9 9-9m-9 9h5m4-9v5M8 3.93552V8m0 0v4m0-4H3.93552M8 8h4m-4 4v4m0-4h4m-4 4v4.0645M8 16H3.93552M8 16h4m0-8v4m0-4h4m-4 4v4m0-4h4m-4 4h4m0-12.06448V8m0 0v4m0-4h4.0645M16 12v4m0 0v4.0645M16 16h4.0645"
                  />
                </svg>
                <span>{currentClient.nationalite}</span>
              </div>
            )}
            {currentClient.adresse && (
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-gray-500"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="m4 12 8-8 8 8M6 10.5V19a1 1 0 0 0 1 1h3v-3a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v3h3a1 1 0 0 0 1-1v-8.5"
                  />
                </svg>
                <span>{currentClient.adresse}</span>
              </div>
            )}
            {currentClient.date_naissance && (
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span>
                  Né(e) le{" "}
                  {getLocalDate(new Date(currentClient.date_naissance))}
                </span>
              </div>
            )}
            {currentClient.date_creation && (
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span>
                  Client depuis le{" "}
                  {getLocalDate(new Date(currentClient.date_creation))}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 p-4">
          {/* Colonne gauche: informations détaillées */}
          <div className="bg-white shadow-md rounded-md p-4 lg:col-span-1">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">
              Informations détaillées
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-4">
              {/* Section Informations personnelles */}
              <div className="bg-gray-50 rounded-md p-3">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  Informations personnelles
                </h3>
                <ul className="space-y-2">
                  <li className="flex justify-between">
                    <span className="text-sm text-gray-500">Nationalité</span>
                    <span className="font-medium">
                      {currentClient.nationalite || "-"}
                    </span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-sm text-gray-500">
                      Type de client
                    </span>
                    <span className="font-medium divide-x-4 divide-gray-400">
                      {currentClient.is_father && (
                        <span className="px-1">Père</span>
                      )}
                      {currentClient.is_mother && (
                        <span className="px-1">Mère</span>
                      )}
                      {currentClient.is_grand_father && (
                        <span className="px-1">Grand-père</span>
                      )}
                      {currentClient.is_grand_mother && (
                        <span className="px-1">Grand-mère</span>
                      )}
                      {!currentClient.is_father &&
                        !currentClient.is_mother &&
                        !currentClient.is_grand_father &&
                        !currentClient.is_grand_mother &&
                        "Standard"}
                    </span>
                  </li>
                  {currentClient.date_ajout && (
                    <li className="flex justify-between">
                      <span className="text-sm text-gray-500">
                        Date d'ajout
                      </span>
                      <span className="font-medium">
                        {getLocalDate(new Date(currentClient.date_ajout))}
                      </span>
                    </li>
                  )}
                </ul>
              </div>

              {/* Section Contacts */}
              <div className="bg-gray-50 rounded-md p-3">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  Contacts
                </h3>
                <ul className="space-y-2">
                  <li className="flex justify-between">
                    <span className="text-sm text-gray-500">
                      Téléphone principal
                    </span>
                    <span className="font-medium">
                      {formatPhoneNumber(currentClient.contact1) || "-"}
                    </span>
                  </li>
                  {currentClient.contact2 && (
                    <li className="flex justify-between">
                      <span className="text-sm text-gray-500">
                        Téléphone secondaire
                      </span>
                      <span className="font-medium">
                        {formatPhoneNumber(currentClient.contact2)}
                      </span>
                    </li>
                  )}
                  <li className="flex justify-between">
                    <span className="text-sm text-gray-500">Email</span>
                    <span className="font-medium">
                      {currentClient.email || "-"}
                    </span>
                  </li>
                </ul>
              </div>

              {/* Section Informations commerciales */}
              <div className="bg-gray-50 rounded-md p-3">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  Informations commerciales
                </h3>
                <ul className="space-y-2">
                  <li className="flex justify-between">
                    <span className="text-sm text-gray-500">
                      Magasin référent
                    </span>
                    <span className="font-medium">
                      {currentClient.magasin_name || "-"}
                    </span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-sm text-gray-500">Commercial</span>
                    <span className="font-medium">
                      {currentClient.commercial_name || "-"}
                    </span>
                  </li>
                  {currentClient.at_event && (
                    <li className="flex justify-between">
                      <span className="text-sm text-gray-500">Événement</span>
                      <span className="font-medium">
                        {currentClient.name_event || "-"}
                      </span>
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>

          {/* Colonnes droite: graphiques et historique */}
          <div className="lg:col-span-2 space-y-4">
            {/* Graphique des achats */}
            <div className="bg-white shadow-md rounded-md p-4">
              <div className="flex item-center justify-between mb-4 border-b pb-3">
                <div>
                  <h2 className="text-lg font-semibold text-gray-800">
                    {
                      {
                        week: "Historique des achats hebdomadaires",
                        month: "Historique des achats mensuels",
                        year: "Historique des achats annuels",
                      }[selectedPeriod]
                    }
                  </h2>
                  <p className="text-sm text-gray-500">
                    Évolution des achats par période
                  </p>
                </div>

                <div className="flex items-center">
                  <span className="text-sm text-gray-500 mr-2">Période :</span>
                  <select
                    className="p-2 border rounded-md bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500 shadow-sm text-sm"
                    name="selectedPeriod"
                    id="selectedPeriod"
                    value={selectedPeriod}
                    onChange={(e) => {
                      setSelectedPeriod(e.target.value);
                    }}
                  >
                    <option value="week">Semaine</option>
                    <option value="month">Mois</option>
                    <option value="year">Année</option>
                  </select>
                </div>
              </div>

              {
                {
                  week: (
                    <ChartFullBg
                      key={"week-" + Math.random()}
                      data={statisticData.week.data}
                      dataLabels={statisticData.week.labels}
                      colors={["#f97316"]}
                      title="Historique des achats de la semaine"
                      tooltipSuffix=" FCFA"
                      period="week"
                    />
                  ),
                  month: (
                    <ChartFullBg
                      key={"month-" + Math.random()}
                      data={statisticData.month.data}
                      dataLabels={statisticData.month.labels}
                      colors={["#fbbf24"]}
                      title="Historique des achats du mois"
                      tooltipSuffix=" FCFA"
                      period="month"
                    />
                  ),
                  year: (
                    <ChartFullBg
                      key={"year-" + Math.random()}
                      data={statisticData.year.data || []}
                      dataLabels={
                        statisticData.year.labels?.map(
                          (month) =>
                            month.charAt(0).toUpperCase() + month.slice(1),
                        ) || []
                      }
                      colors={["#84cc16"]}
                      title="Historique des achats de l'année"
                      tooltipSuffix=" FCFA"
                      period="year"
                    />
                  ),
                }[selectedPeriod]
              }

              {/* Information supplémentaire sous le graphique */}
              <div className="mt-4 p-3 bg-amber-50 rounded-md border border-amber-100 text-sm text-amber-700 flex items-center">
                <svg
                  className="w-5 h-5 mr-2 text-amber-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>
                  Les données présentées sont basées sur les transactions
                  enregistrées dans le système.
                </span>
              </div>
            </div>

            {/* Historique des achats */}
            <div className="bg-white shadow-md rounded-md">
              <Purchases purchases={purchases} getLocalDate={getLocalDate} />
            </div>
          </div>
        </div>

        {/* Section des dégustations */}
        <div className="p-4" data-section="degustations">
          <ClientDegustations
            clientId={currentClient.id}
            clientName={`${currentClient.nom} ${currentClient.prenom || ""}`}
            magasinId={currentClient.magasin}
          />
        </div>
      </>
    )
  );
}
